import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { api } from "../services/api";
import { useLogged } from "./userLogin";
import { AxiosError, AxiosResponse } from "axios";

interface IProps {
  children: ReactNode;
}

interface ICreateCompanyDTO {
  name: string;
  document: string;
  description: string;
  primary_color: string;
  secondary_color: string;
  logo: string;
}

interface ICompany extends ICreateCompanyDTO {
  id: string,
  created_by?: string
}

interface ICompaniesContext {
  companies: ICompany[];
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  isNewModalVisible: boolean;
  isEditModalVisible: boolean;
  handleModalVisible: (visible: boolean) => void;
  handleEditModalVisible: (visble: boolean) => void;
  fetchCompanies: (
    page: number,
    order: string,
    orderBy: string,
    search?: string
  ) => Promise<void>;
  fetchAllCompanies: (search?: string) => Promise<ICompany[]>;
  deleteCompany: (id: string) => Promise<AxiosResponse | AxiosError>;
  createCompany: (role: ICreateCompanyDTO) => Promise<AxiosResponse | AxiosError>;
  updateCompany: (
    id: string,
    body: ICreateCompanyDTO
  ) => Promise<AxiosResponse | AxiosError>;
}

export const CompaniesContext = createContext<ICompaniesContext>(
  {} as ICompaniesContext
);

export const CompaniesStorage = ({ children }: IProps) => {
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isNewModalVisible, setINewModalVisible] = useState(false);
  const [isEditModalVisible, setEditModalVisible] = useState(false);

    const { token, user } = useLogged();

    async function handleModalVisible(visible: boolean) {
      setINewModalVisible(visible);
    }

    async function handleEditModalVisible(visible :boolean){
      setEditModalVisible(visible);
    }

    async function fetchCompanies(
      page: number,
      order: string,
      orderBy: string,
      search?: string
    ) {
      setLoading(true);
      setError(null);

      api
        .get(
          search === undefined || !search
            ? `/companies?page=${page}&items=7&columnOrder=${orderBy}&order=${order}`
            : `/companies?page=${page}&items=7&columnOrder=${orderBy}&order=${order}&search=${search}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setCompanies(response.data.data.companies);
          setTotal(response.data.data.companiesNumber);
          setTotalPages(response.data.data.totalPages);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    async function fetchAllCompanies(search?: string): Promise<ICompany[]> {
      try {
        const response = await api.get(
          search
            ? `/companies?page=1&items=0&columnOrder=name&order=asc&search=${search}`
            : `/companies?page=1&items=0&columnOrder=name&order=asc`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data.data.companies;
      } catch (err) {
        setError(err);
        return [];
      }
    }

    async function createCompany({
      name,
      document,
      description,
      primary_color,
      secondary_color,
      logo,
    }: ICreateCompanyDTO): Promise<AxiosResponse | AxiosError> {
      let response: AxiosResponse | AxiosError;

      try {
        setLoading(true);
        setError(null);

        response = await api.post(
          "/companies",
          {
            name,
            document,
            description,
            primary_color,
            secondary_color,
            logo,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        await fetchCompanies(1, "asc", "name");
      } catch (err) {
        response = err as AxiosError;
        setError(err);
      } finally {
        setLoading(false);
      }

      return response;
    }

    async function deleteCompany(id: string): Promise<AxiosResponse | AxiosError> {
      let response: AxiosResponse | AxiosError;

      try {
        setLoading(true);
        setError(null);

        response = await api.delete(`/companies/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        await fetchCompanies(1, "asc", "name");
      } catch (err) {
        response = err as AxiosError;
        setError(err);
      } finally {
        setLoading(false);
      }

      return response;
    }

    async function updateCompany(
      id: string,
      {
        name,
        document,
        description,
        primary_color,
        secondary_color,
        logo,
      }: ICreateCompanyDTO
    ): Promise<AxiosResponse | AxiosError> {
      let response: AxiosResponse | AxiosError;

      setLoading(true);
      setError(null);

      try {
        setLoading(true);
        setError(null);

        response = await api.put(
          `/companies/${id}`,
          {
            name,
            document,
            description,
            primary_color,
            secondary_color,
            logo,

          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

      } catch (err) {
        response = err as AxiosError;
        setError(err);
      } finally {
        setLoading(false);
      }

      return response;
    }

    useEffect(() => {
      fetchCompanies(1, "asc", "name");
    }, [token, user]);

  return (
    <CompaniesContext.Provider
      value={{
        companies,
        error,
        loading,
        total,
        totalPages,
        isNewModalVisible,
        isEditModalVisible,
        handleModalVisible,
        handleEditModalVisible,
        createCompany,
        deleteCompany,
        fetchCompanies,
        fetchAllCompanies,
        updateCompany,
      }}
    >
      {children}
    </CompaniesContext.Provider>
  );
};

export function useCompanies() {
  const context = useContext(CompaniesContext);

  return context;
}
